import { createWalletClient, custom, getAddress, isAddress, parseAbi, zeroAddress, type Address, type EIP1193Provider, type Hash } from 'viem';
import { bsc, bscTestnet } from 'viem/chains';

/*
 * Owner instruction 2026-09-24 (b): every V2 parameter is adjustable from the admin panel. The
 * connected wallet signs the governance call; the contracts only accept their owner (application
 * governance), so a non-owner wallet gets a revert, never a silent no-op.
 */

export const MATRIX_ABI = parseAbi([
  'function setUplineSearchLimit(uint16 limit)',
  'function setRoutingLimits(uint16 uplineLimit, uint8 generations, uint8 branches)',
  'function setEconomics(uint256 slotOneValue, uint8 qualifyingDirects, uint256[10] sponsorLevelAmounts)',
  'function setDormancy(uint64 dormancySeconds, uint64 reclaimSeconds)',
  'function sweepDormant(uint256 bDynamicId)',
  'function forfeitDormant(uint256 bDynamicId)',
]);
export const VAULT_ABI = parseAbi([
  'function setPayoutParameters(uint16 rewardFeeBps, uint16 sponsorFeeBps, bool pushPayouts)',
  'function setPoolFees(uint16 poolFeeBps, uint16 withdrawalFeeBps, uint256 gasReserveMinimum)',
  'function setLiquidationFee(uint16 bps)',
  'function claim(uint256 bDynamicId)',
]);
export const CORE_ABI = parseAbi([
  'function setEraTimings(uint64 qualificationPeriod, uint64 transitionFreeze)',
  'function setStandardEntry(uint256 standardEntry)',
  'function setWalletIdLimit(uint32 newLimit)',
]);
/** BDynamicMedianOracle governance (rule 16 s.F 414-415): fresh-source quorum and maximum source age. */
export const ORACLE_GOV_ABI = parseAbi(['function setSafetyParameters(uint8 minimumSources, uint64 maximumSourceAge)']);

export interface GovernanceAddresses {
  chainId: 56 | 97;
  core: Address | null;
  treasury: Address | null;
  matrix: Address | null;
  leadersClub: Address | null;
  token: Address | null;
  cdttProvider: Address | null;
  /** BDynamicCdtBenefit (docs/CDT_BENEFIT_SPEC.md): from the live params (params.cdtBenefit.address), VITE_CDT_BENEFIT_ADDRESS as fallback. */
  cdtBenefit?: Address | null;
}

function configuredAddress(value: string | undefined): Address | null {
  if (!value || !isAddress(value)) return null;
  const address = getAddress(value);
  return address === zeroAddress ? null : address;
}

export function resolveGovernanceAddresses(env: ImportMetaEnv = import.meta.env): GovernanceAddresses {
  const chainId = Number(env.VITE_CHAIN_ID ?? 97) === 56 ? 56 : 97;
  return {
    chainId,
    core: configuredAddress(env.VITE_PROTOCOL_PROXY_ADDRESS),
    treasury: configuredAddress(env.VITE_TREASURY_PROXY_ADDRESS),
    matrix: configuredAddress(env.VITE_MATRIX_PROXY_ADDRESS),
    leadersClub: configuredAddress(env.VITE_LEADERS_CLUB_PROXY_ADDRESS),
    token: configuredAddress(env.VITE_UTILITY_TOKEN_PROXY_ADDRESS),
    cdttProvider: configuredAddress(env.VITE_CDTT_PROVIDER_ADDRESS),
    cdtBenefit: configuredAddress(env.VITE_CDT_BENEFIT_ADDRESS),
  };
}

export async function sendGovernanceCall(
  addresses: GovernanceAddresses,
  account: Address,
  target: 'core' | 'treasury' | 'matrix' | 'leadersClub' | 'token' | 'cdttProvider' | 'cdtBenefit',
  abi: any,
  functionName: string,
  args: readonly unknown[],
): Promise<Hash> {
  if (!window.ethereum) throw new Error('No injected EVM wallet is available.');
  const address = addresses[target];
  if (!address) throw new Error(`${target} contract address is not configured.`);
  const walletClient = createWalletClient({ account, chain: addresses.chainId === 56 ? bsc : bscTestnet, transport: custom(window.ethereum as unknown as EIP1193Provider) });
  return walletClient.writeContract({ address, abi, functionName, args } as never);
}

/**
 * Signs a governance call on a contract whose address comes from the live parameters (e.g. the oracle the Core
 * contract actually uses), not from the build-time environment.
 */
export async function sendContractCall(chainId: 56 | 97, account: Address, address: string | null | undefined, abi: any, functionName: string, args: readonly unknown[]): Promise<Hash> {
  if (!window.ethereum) throw new Error('No injected EVM wallet is available.');
  if (!address || !isAddress(address)) throw new Error('The contract address is not known yet (live parameters not loaded).');
  const walletClient = createWalletClient({ account, chain: chainId === 56 ? bsc : bscTestnet, transport: custom(window.ethereum as unknown as EIP1193Provider) });
  return walletClient.writeContract({ address: getAddress(address), abi, functionName, args } as never);
}

/**
 * Leaders Club on-chain (rules 13 / 16 / 19): anyone may call settle() once the period is due; the period is the
 * contract's (mode 0 = fixed seconds, at least 60 s; mode 1 = calendar month closing 12 midnight IST), set by the owner.
 */
export const LEADERS_CLUB_ABI = parseAbi(['function settle() returns (uint256)', 'function setPeriod(uint8 mode, uint64 periodSeconds)']);

/*
 * Owner 2026-10-11: the admin panel shows ONLY contract values. These extend the shared EngineParameters type with
 * the sections apps/api/src/onchain/params.ts now reads live (all optional so an older API still renders).
 */
export interface LiveEngineExtras {
  errors?: string[];
  readAt?: string;
  addresses: { leadersClub?: string | null; cdttProvider?: string | null; oracle?: string | null; cdtBenefit?: string | null };
  governance: { tokenOwner?: string | null; leadersClubOwner?: string | null; cdttProviderOwner?: string | null; settlementOwner?: string | null; oracleOwner?: string | null; settlementSigners?: string[]; cdtBenefitOwner?: string | null };
  matrix: { subSlotValuesAtomic?: { half: string; quarter: string } | null; tierHook?: string | null; clubHook?: string | null } | null;
  core: {
    effectiveEra?: number; joiningFrozen?: boolean; frozenUntil?: string | null; maxIdsPerWallet?: number; maximumObservationGapSeconds?: number; priceOracle?: string | null;
    entryAmountsAtomic?: { standard: string; half: string; quarter: string }; triggerPricesE18?: { half: string; quarter: string };
    progress?: { targetEra: number; pending: boolean; qualificationStartedAt: string | null; lastObservationAt: string | null; lastPriceE18: string; freezeEndsAt: string | null };
  } | null;
  leadersClub?: { periodMode: number; periodSeconds: number; nextSettlementAt: string | null; settlementDue: boolean; pendingFundAtomic: string; totalCarryAtomic: string; totalPaidAtomic: string; clubs: Array<{ club: number; members: number; carryAtomic: string }> } | null;
  cdtt?: { cdttPerBtcbAtomic: string; multiplierBps: number; minMultiplierBps: number; maxMultiplierBps: number; priceOracle: string | null; cdtPerUsdAtomic: string; effectiveParityAtomic: string; parityLive: boolean } | null;
  oracle?: { minimumSources: number; maximumSourceAgeSeconds: number; sourceCount: number; latest: { priceE18: string; observedAt: string | null; sourceCount: number } | null } | null;
}

/** GET /api/<slug>/onchain/records (apps/api/src/db/chainRecords.ts): indexed contract events only. */
export interface ChainRecords {
  era: { currentEra: string | null; events: Array<{ kind: string; targetEra: string | null; previousEra: string | null; observedAt: string | null; priceE18: string | null; reason: string | null; freezeStartedAt: string | null; freezeEndsAt: string | null; activatedAt: string | null; at: string; txHash: string }> };
  oracle: { sources: Array<{ sourceId: string; reporter: string; active: boolean; at: string; txHash: string }>; prices: Array<{ sourceId: string; reporter: string; priceE18: string; observedAt: string; at: string; txHash: string }> };
  parameters: { current: Array<{ key: string; value: string; at: string | null; txHash: string | null }>; history: Array<{ contractKind: string; key: string; value: string; at: string; txHash: string }> };
  cdttCredits: Array<{ redemptionId: number; bdId: string; tokenId: string | null; wallet: string | null; tokenValueAtomic: string | null; cdttAtomic: string | null; creditedAtomic: string | null; multiplierBps: number | null; at: string; txHash: string }>;
  upgradeResets: Array<{ bdId: string; slot: number; seatsToRefill: number; positionsReopened: boolean; at: string; txHash: string }>;
  tokenEvents: Array<{ tokenId: string; bdId: string | null; kind: string; tier: number | null; amountAtomic: string | null; address: string | null; at: string; txHash: string }>;
  unknownEvents: Array<{ contractKind: string; eventName: string; count: number }>;
}

/** Whole-number decimal string -> 18-decimal atomic (no float rounding). */
export function decimalToAtomic(value: string, decimals = 18): bigint {
  const trimmed = value.trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) throw new Error(`"${value}" is not a positive decimal number.`);
  const [whole = '0', fraction = ''] = trimmed.split('.');
  return BigInt(whole) * 10n ** BigInt(decimals) + BigInt((fraction + '0'.repeat(decimals)).slice(0, decimals) || '0');
}

/** Primary Token: governance freeze period (testnet time compression) and the public unfreeze call. */
/** CDTT conversion provider (rules 23 / 36): admin parity + multiplier, and the live price source. */
export const CDTT_PROVIDER_GOV_ABI = parseAbi(['function setParameters(uint256 cdttPerBtcb, uint32 multiplierBps)', 'function setPriceSource(address priceOracle, uint256 cdtPerUsd)']);

export const UTILITY_TOKEN_ABI = parseAbi(['function setFreezePeriod(uint64 freezeSeconds)', 'function unfreeze(uint256 tokenId)']);

/**
 * docs/CDT_BENEFIT_SPEC.md s.1 "Governance" - BDynamicCdtBenefit (owner = application governance). Sent with
 * sendContractCall to params.cdtBenefit.address; only the contract owner's wallet is accepted. The member calls are in
 * lib/cdt-benefit.ts.
 */
export const CDT_BENEFIT_ABI = parseAbi([
  'function setParameters(uint32 multiplierBps, uint32 scheduleDays, bool accrualFromJoin)',
  'function setCountryCurrency(bytes32 country, bytes32 currency)',
  'function setReferenceValue(bytes32 currency, uint256 minorUnits)',
  'function setCdtRate(bytes32 currency, uint256 cdtPerMinorUnit)',
  'function setCountryFor(uint256 id, bytes32 country)',
  'function setSettlement(address settlement, address liquidityLedger)',
  'function selectCountry(uint256 id, bytes32 country)',
  'function claim(uint256 id)',
  'function convert(uint256 redemptionId)',
  // v2 (owner settlement workflow 2026-10-21): daily FX keeper + settlement / activation helpers (anyone may call the last two).
  'function setRateReporter(address reporter, uint64 maxRateAgeSeconds)',
  'function refreshCurrency(bytes32 currency, uint256 minorUnits, uint256 cdtPerMinorUnit)',
  'function settleOptionA(uint256 id, uint256 redemptionId)',
  'function activate(uint256 id)',
  // v2 security fixes (2026-10-21): settle delay after maturity, keeper rate bounds, owner void (unsettled, nothing claimed), forfeit record.
  'function setSettleDelay(uint64 settleDelaySeconds)',
  'function setRateBounds(uint32 maxDeviationBps, uint256 maxTargetCdt)',
  'function voidSchedule(uint256 id)',
  'function settleForfeit(uint256 id)',
  // Bridge (2026-10-22): the CDT Platform pool claimTo(id, pool) imports into ("Import to CDT Platform"); owner-set.
  'function setCdtPlatformPool(address pool)',
  'function claimTo(uint256 id, address to)',
  'function cdtPlatformPool() view returns (address)',
  'function importedCdt(uint256 id) view returns (uint256)',
  'function owner() view returns (address)',
  'function rateReporter() view returns (address)',
  'function maxRateAgeSeconds() view returns (uint64)',
  'function settlementStatus(uint256 id) view returns (uint8)',
  'function lockedBalance(uint256 id) view returns (uint256)',
  'function voidedCdt(uint256 id) view returns (uint256)',
  'function benefitQuote(uint256 id) view returns (bytes32 country, bytes32 currency, uint256 referenceMinor, uint256 targetCdt, uint256 dailyCdt, uint32 scheduleDays, bool ready)',
  'function claimable(uint256 id) view returns (uint256)',
  'function accrued(uint256 id) view returns (uint256)',
]);
